import * as React from 'react';

/** A promo of the business that applies by itself (Promos in the panel), as the catalog lists it. */
export type Promotion = {
  id: number; name: string; description: string; label: string; type: 'percentage' | 'amount' | 'free'; value: number;
  /** The product it's on (null: the whole order), the products the order needs, and its minimum. */
  product: number | null; conditions: number[]; minimum: number; ends_at: string;
};

export type QuoteReward = {
  id: number; title: string; description: string; expires_at: string;
  /** Applies to this cart, what it takes off (or the gifts it gives), and what to add when it doesn't. */
  usable: boolean; amount: number; gifts: string[]; problem?: 'requires' | 'nothing' | 'partial' | null; hint: string;
};

/** What the cart costs with the business's promos, and the customer's rewards with their card on this phone (the
 * phone typed must be the card's). The server computes it again when the order is sent. */
export type Quote = {
  subtotal: number; promotions: { id: number; name: string; label: string; amount: number }[]; discount: number;
  rewards: QuoteReward[]; best_reward: number | null; fee: number; service_fee: number; amount: number;
  /** Whose card is on this phone, how its phone ends and whether the phone typed is it (null: not typed yet). */
  card?: { first_name: string; phone_end: string; phone_matches: boolean | null } | null;
};

/** The short badge of a promo on its product: "−15%", "−$20" or "Regalo". */
export const promoBadge = (promo: Promotion) =>
  promo.type === 'percentage' ? `−${Math.round(Number(promo.value) * 10000) / 100}%`
    : promo.type === 'amount' ? `−$${Number(promo.value).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`
      : 'Regalo';

/** "Hasta el 30 de noviembre". */
export const promoUntil = (promo: Promotion) =>
  `Hasta el ${new Date(promo.ends_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' })}`;

/**
 * Asks the server for the quote of `body` (the order as it would be sent) while `enabled`, once it stops changing.
 * `current` says whether the quote is for the body as it is now; null on errors (the order goes without it: the
 * server applies the promos anyway).
 */
export const useQuote = (slug: string, enabled: boolean, body: object) => {
  const key = JSON.stringify(body);
  const [state, setState] = React.useState<{ key: string; quote: Quote | null } | null>(null);
  React.useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const timer = setTimeout(() => {
      fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/quote/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: key,
      })
        .then(async response => {
          const data = response.ok ? await response.json().catch(() => null) : null;
          if (alive) setState({ key, quote: data });
        })
        .catch(() => { if (alive) setState({ key, quote: null }); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [enabled, key, slug]);
  return { quote: enabled ? state?.quote ?? null : null, current: !enabled || state?.key === key };
};
